import { useEffect } from 'react';

import { company } from '~/company/Company';
import { pickupStoreLinkState } from '~/services/pickupStoreLinkState';
import { mainStore, userStore } from '~/stores';
import SessionStorage from '~/stores/SessionStorage';
import {
  getIsMandatoryAddressSelection,
  getMandatoryAddressModalType,
} from '~/utils/mandatoryAddress';

import { useModal } from './useModal';

const FIRST_VISIT_ADDRESS_MODAL_SESSION_KEY = 'first_visit_address_modal_shown';

/**
 * Opens the delivery address modal automatically on the user's first visit
 * this session, so they can pick an address and land in the catalog of the
 * correct warehouse. Shown once per browser session and again on the next
 * session if the address is still not set; skipped while RN onboarding is
 * active or when an address is already selected.
 *
 * No-op for companies whose `autoOpenAddressModalOnFirstVisit` flag makes the
 * flow mandatory — the blocking gate in `App.tsx` is the sole opener there.
 */
export function useFirstVisitAddressModal(): void {
  const { openModal } = useModal();

  useEffect(() => {
    if (!company.autoOpenAddressModalOnFirstVisit) {
      return;
    }
    // Not merely "the blocking gate owns this": that gate reports false while a
    // store link is resolving, which would drop this hook straight through to
    // opening the very modal the link exists to skip — and burn the
    // once-per-session flag doing it.
    if (pickupStoreLinkState.isPending) {
      return;
    }
    if (getIsMandatoryAddressSelection()) {
      return;
    }
    if (!mainStore.isAllStoresSynchronized) {
      return;
    }
    if (mainStore.isRN && !mainStore.onboardingSeen) {
      return;
    }
    if (SessionStorage.get(FIRST_VISIT_ADDRESS_MODAL_SESSION_KEY)) {
      return;
    }
    if (userStore.deliveryAddress) {
      SessionStorage.set(FIRST_VISIT_ADDRESS_MODAL_SESSION_KEY, true);
      return;
    }

    openModal(getMandatoryAddressModalType());
    SessionStorage.set(FIRST_VISIT_ADDRESS_MODAL_SESSION_KEY, true);
  }, [mainStore.isAllStoresSynchronized, pickupStoreLinkState.isPending]);
}
